import { useMemo, useState } from 'react';

import { Button as MuiButton, Stack, Typography } from '@mui/material';

import ClaimScreen, { ClaimBar } from '@/james-playground/aa-family-claim/ClaimScreen';
import ConflictChooser from '@/james-playground/aa-family-claim/ConflictChooser';
import ClaimFactList, {
  type FactGroup,
  type ReviewRow,
} from '@/james-playground/aa-family-claim/ClaimFactList';
import { AREAS, OWNER, say } from '@/james-playground/aa-family-claim/claim/asks';
import { voiceOf, type ClaimState } from '@/james-playground/aa-family-claim/claim/claim';
import { CHECK } from '@/james-playground/aa-family-claim/claim/claim.content';
import { statusOf, toCheck } from '@/james-playground/aa-family-claim/claim/engine';
import {
  SOURCE_LABEL,
  answer,
  confirm,
  settle,
  type Facts,
} from '@/james-playground/aa-family-claim/claim/facts';
import { labelOf, valueText } from '@/james-playground/aa-family-claim/claim/labels';
import { ENTRIES, ENTRY, isAsked, type Entry } from '@/james-playground/aa-family-claim/claim/registry';

/** How a row is edited in the list: tables and grids change in their round. */
const controlOf = (e: Entry): Pick<ReviewRow, 'control' | 'choices'> => {
  if (e.answerType === 'yes_no' || e.answerType === 'single_choice')
    return { control: 'choice', choices: e.options?.map((o) => o.label) };
  if (e.answerType === 'multi_choice') return { control: 'multi', choices: e.options?.map((o) => o.label) };
  return { control: 'text' };
};

const rowFor = (e: Entry, facts: Facts, claim: ClaimState): ReviewRow => {
  const v = voiceOf(claim);
  const fact = facts[e.id];
  const source = fact ? { label: SOURCE_LABEL[fact.source], quote: fact.quote } : undefined;
  const base = {
    id: e.id,
    label: labelOf(e.id, v),
    value: valueText(e.id, fact?.value),
    note: e.q ? CHECK.onForm(e.q.split('.')[0]) : undefined,
    source,
    ...controlOf(e),
  };
  return { ...base, from: fact?.state === 'proposed' ? 'ai' : undefined };
};

/** Group rows by hub area, in hub order. */
const byArea = (entries: Entry[], facts: Facts, claim: ClaimState): FactGroup[] => {
  const v = voiceOf(claim);
  return AREAS.map((area) => ({
    label: say(area.label, v),
    rows: entries.filter((e) => OWNER[e.id]?.area === area.id).map((e) => rowFor(e, facts, claim)),
  })).filter((g) => g.rows.length);
};

/**
 * The Check step (spec 8): every proposed or conflicting answer, with where it
 * came from and which question it fills, confirmed before anything is sent.
 * The rest of the form is a collapsed read-through.
 */
const ReviewScreen = ({
  claim,
  onChange,
  onBack,
  onNext,
}: {
  claim: ClaimState;
  onChange: (claim: ClaimState) => void;
  onBack: () => void;
  onNext: () => void;
}) => {
  const { facts } = claim;
  // The list to check is fixed when the screen opens, so a row confirmed here
  // stays in view with its new state instead of vanishing under the thumb.
  const [checking] = useState(() => toCheck(facts).map((e) => e.id));
  const open = toCheck(facts);

  // Disagreements are settled above the list, whatever their answer type, and
  // join the list once they are.
  const disagreeing = open.filter((e) => facts[e.id]?.state === 'conflict');
  const groups = useMemo(
    () =>
      byArea(
        checking.map((id) => ENTRY[id]).filter((e) => facts[e.id]?.state !== 'conflict'),
        facts,
        claim,
      ),
    [checking, facts, claim],
  );
  const rest = useMemo(
    () =>
      byArea(
        ENTRIES.filter(
          (e) => isAsked(e) && !checking.includes(e.id) && statusOf(e, facts) === 'confirmed',
        ),
        facts,
        claim,
      ),
    [checking, facts, claim],
  );

  const change = (id: string, value: string) => {
    const e = ENTRY[id];
    // Tables and grids are changed in their own round, from the hub.
    if (!e || e.answerType === 'table') return;
    const next = e.answerType === 'multi_choice' ? value.split(', ').filter(Boolean) : value;
    onChange({ ...claim, facts: answer(facts, id, next) });
  };

  const confirmAll = () =>
    onChange({
      ...claim,
      facts: open.reduce((f, e) => (f[e.id]?.state === 'proposed' ? confirm(f, e.id) : f), facts),
    });

  const conflicts = open.filter((e) => facts[e.id]?.state === 'conflict').length;

  return (
    <ClaimScreen
      bar={<ClaimBar onBack={onBack} />}
      title={CHECK.title}
      description={CHECK.description}
      footer={
        open.length ? (
          <>
            {conflicts > 0 && (
              <Typography role='status' sx={{ textAlign: 'center' }} variant='body2'>
                {CHECK.settleFirst(conflicts)}
              </Typography>
            )}
            <MuiButton variant='contained' size='large' fullWidth onClick={confirmAll} disabled={open.length === conflicts}>
              {CHECK.confirmAll}
            </MuiButton>
          </>
        ) : (
          <MuiButton variant='contained' size='large' fullWidth onClick={onNext}>
            {CHECK.next}
          </MuiButton>
        )
      }
    >
      {disagreeing.length > 0 && (
        <Stack spacing={2}>
          <Typography component='h2' variant='h4'>
            {CHECK.clash}
          </Typography>
          {disagreeing.map((e) => (
            <ConflictChooser
              key={e.id}
              id={e.id}
              facts={facts}
              claim={claim}
              bubble={false}
              onPick={(keep) => onChange({ ...claim, facts: settle(facts, e.id, keep) })}
            />
          ))}
        </Stack>
      )}
      {groups.length > 0 && (
        <Stack spacing={1.5}>
          <Typography component='h2' variant='h4'>
            {CHECK.toCheck}
          </Typography>
          <ClaimFactList groups={groups} edit='drawer' save='explicit' onChange={change} />
        </Stack>
      )}
      {rest.length > 0 && (
        <Stack spacing={1.5}>
          <Typography component='h2' variant='h4'>
            {CHECK.everythingElse}
          </Typography>
          <ClaimFactList groups={rest} collapsible edit='drawer' save='explicit' onChange={change} />
        </Stack>
      )}
    </ClaimScreen>
  );
};

export default ReviewScreen;
